import React, { useCallback, useEffect, useState } from 'react';
import { DialogContent, DialogContentText } from '@material-ui/core';
import {
  BulkActionsSlide,
  CurrentGuestStays,
  FutureGuestStays,
  MyActivity,
  ResponsiveContainer,
  StyledDialog,
  useCurrentReservations,
  useCurrentUserId,
  useFormWatch,
  useFutureReservations,
  useMyAccounts,
  useObservable,
} from '@lib/common';
import {
  reservationService,
  activityReportService,
  StayInfoFilters,
  activityReportQuery,
  ReservationForms,
  FormStatus,
  Reservation,
  getGuests,
  corporateAccountService,
  bulkAvailableRateQuery,
  bulkAvailableService,
} from '@lib/state';
import { StayInfoFilter } from '../components/stay-info-filter';

enum RejectionReason {
  NoAffiliation = 'No affiliation',
  GuestExists = 'Guest already exists in the selection',
  DifferentProperty = 'Property is not the same as the bulk selection',
  DifferentCorp = 'Corporate account is not the same as the bulk selection',
  NotFullyInvoiced = 'Reservation has charges that are not invoiced',
  Accept = 'Acceptable reservation',
}

export const StayInfoPage: React.FC = () => {
  const bulkFilters = useObservable(bulkAvailableRateQuery.filters);

  const [cannotSelectReason, setCannotSelectReason] = useState<string>('');

  const filters = useObservable(activityReportQuery.filters);
  const userId = useCurrentUserId();
  const current = useCurrentReservations(filters);
  const future = useFutureReservations(filters);
  const myCorporateAccounts = useMyAccounts();

  const shouldSelectReservation = useCallback(
    (reservation: Reservation): RejectionReason => {
      if (!reservation.affiliation?.corporateAccountId) {
        return RejectionReason.NoAffiliation;
      }

      if (!reservation.isFullyInvoiced) {
        return RejectionReason.NotFullyInvoiced;
      }

      const sameGuest = bulkFilters.selectedReservations.some(r => {
        const selectedGuests = getGuests(r);
        const reservationGuests = getGuests(reservation);

        if (selectedGuests && reservationGuests) {
          return selectedGuests.some(selectedGuest => {
            return reservationGuests.some(reservationGuest => {
              const sameEmail = reservationGuest.contact.email === selectedGuest.contact.email;
              const sameEmployeeNumber =
                reservationGuest.employeeNumber &&
                selectedGuest.employeeNumber &&
                reservationGuest.employeeNumber === selectedGuest.employeeNumber;

              return sameEmail || sameEmployeeNumber;
            });
          });
        }

        return false;
      });

      if (sameGuest) {
        return RejectionReason.GuestExists;
      }

      if (!!bulkFilters.propertyId && reservation.propertyId !== bulkFilters.propertyId) {
        return RejectionReason.DifferentProperty;
      }

      if (
        !!bulkFilters.corporateAccountId &&
        reservation.affiliation.corporateAccountId !== bulkFilters.corporateAccountId
      ) {
        return RejectionReason.DifferentCorp;
      }

      return RejectionReason.Accept;
    },
    [bulkFilters]
  );

  const onSelectionChanged = useCallback(
    (reservation: Reservation) => {
      if (bulkFilters.selectedReservations.find(r => r.id === reservation.id)) {
        bulkAvailableService.deselectReservation(reservation);
      } else {
        const selectionResult = shouldSelectReservation(reservation);
        if (selectionResult === RejectionReason.Accept) {
          bulkAvailableService.selectReservation(reservation);
        } else {
          setCannotSelectReason(selectionResult);
        }
      }
    },
    [bulkFilters, shouldSelectReservation]
  );

  const lastEvent = useFormWatch(
    ReservationForms.UpdateCostCenterFields,
    ReservationForms.UpdateReservation,
    ReservationForms.CancelReservation,
    ReservationForms.BulkCancel,
    ReservationForms.LateCheckOut,
    ReservationForms.CheckOutReservation
  );

  const onExport = useCallback(
    status =>
      reservationService.exportUserReservations({
        status,
      }),
    []
  );

  const onChange = useCallback((update: StayInfoFilters) => {
    activityReportService.getMyActivity({
      ...update,
    });
  }, []);

  useEffect(() => {
    if (lastEvent?.status === FormStatus.Success) {
      onChange(filters);
    }
  }, [onChange, lastEvent, filters]);

  useEffect(() => {
    corporateAccountService.getMyAccounts();
  }, []);

  return (
    <ResponsiveContainer>
      <BulkActionsSlide />
      <StyledDialog
        title="Reservation Selection Conflict"
        onClose={() => setCannotSelectReason('')}
        open={!!cannotSelectReason}
      >
        <DialogContent>
          <DialogContentText>{cannotSelectReason}</DialogContentText>
        </DialogContent>
      </StyledDialog>
      <StayInfoFilter
        filters={filters}
        onChange={onChange}
        userId={userId}
        corporateAccounts={myCorporateAccounts}
      />
      <CurrentGuestStays
        userId={userId}
        reservations={current.reservations}
        page={current.page}
        onLoadMore={current.loadMore}
        onExport={onExport}
        showActions
        createForGuest={false}
        onBulkSelectionChanged={onSelectionChanged}
        selectedReservations={bulkFilters.selectedReservations}
        bulkCorporateAccountSelected={bulkFilters.corporateAccountId}
        bulkPropertySelected={bulkFilters.propertyId}
      />
      <FutureGuestStays
        userId={userId}
        reservations={future.reservations}
        page={future.page}
        onLoadMore={future.loadMore}
        onExport={onExport}
        showActions
        createForGuest={false}
        onBulkSelectionChanged={onSelectionChanged}
        selectedReservations={bulkFilters.selectedReservations}
        bulkCorporateAccountSelected={bulkFilters.corporateAccountId}
        bulkPropertySelected={bulkFilters.propertyId}
      />
      <MyActivity bookedBy={filters.bookedBy} showActions createForGuest={false} />
    </ResponsiveContainer>
  );
};
